import React, { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router"
import useTitle from "@/hooks/useTitle"
import EtBtnFormBasic from "@/components/common/buttons/EtBtnFormBasic"
import PulseLoader from "react-spinners/PulseLoader"
import EtContentEditorLightWithPlace from "@/utils/EtContentEditorLightWithPlace"
import EtContentEditorLight3 from "@/utils/EtContentEditorLight3"
import { useGetAssessmentsQuery, useUpdateAssessmentMutation } from "./assessmentsApiSlice"
import { useGetSectionsQuery } from "../sections/sectionsApiSlice"

const EtUpdateAssessment = ({id, setActiveAdminView}) => {
  // Created - 15 Apr 2024
  // Author - Atul Pandey
  // Updated - 19 Aug 2025 - switched from navigate to setActiveAdminView

  // 1 - Set Title
  useTitle("Update an Assessment")

  // 2 - extract the assessment Id from the url
  // const { id } = useParams()
  // console.log("id:", id)

  // 3 - Extract the assessment from the query to load the assessment data
  const { assessment } = useGetAssessmentsQuery("Assessment", {
    selectFromResult: ({ data }) => ({
      assessment: data?.entities[id],
    }),
  })

  // 4 - Check for returned data
  if (!assessment) {
    return <PulseLoader color={"#ea580c"} />
  }

  // 5. Display data
  let content
  content = <EtUpdateAssessmentForm assessment={assessment} id={id} setActiveAdminView={setActiveAdminView}/> // 19 Aug 2025 - added setActiveAdminView={setActiveAdminView}

  return content
}

const EtUpdateAssessmentForm = ({assessment, id, setActiveAdminView}) => {
  // 1 - set the variable for navigate
  const navigate = useNavigate()

  // 2 - Define the mutation to update the section
  const [updateAssessment, { isLoading, isSuccess, isError, error }] =
    useUpdateAssessmentMutation()
  
  // 3 - Fill the form data with extracted assessment info
  const [formData, setFormData] = useState({
    assessmentName: assessment.assessmentName,
    assessmentIntro: assessment.assessmentIntro,
    assessmentOutro: assessment.assessmentOutro,
    aboutMe: assessment.aboutMe,
    sections: assessment.sections || [],
  })

  // 3a - get all sections
  const { sections } = useGetSectionsQuery('sectionList', {
    selectFromResult: ( { data }) => ({
      sections: data?.ids.map(id => data?.entities[id])
    })
  })
  
  //console.log('sections:', sections)

  // 4 - New state to hold the currently selected section Id
  const [selectedSection, setSelectedSection] = useState("")
  const [allSectionsAdded, setAllSectionsAdded] = useState(false)

  // 5 - function to strip html tags from sections
  function stripHtml(html) {
  return html.replace(/<\/?[^>]+(>|$)/g, "");
  }

  // 5a - Create options to select a section from list of all sections
  const sectionOptions = sections ? [
  <option key="placeholder" value="" disabled selected>
    Select a section
  </option>,
  ...sections?.map(section => (
    <option key={section.id} value={section.id}>
      {stripHtml(section.sectionName)}
    </option>
  ))
  ] : []

  // 6 - Reset form upon successful submission
  useEffect(() => {
  if(isSuccess) {
    setFormData({
      assessmentName: "",
      assessmentIntro: "",
      assessmentOutro: "",
      aboutMe: "",
      pic: "",
      sections: [],
    })

    // navigate('/assessments')
    setActiveAdminView('assessments'); // 19 Aug 2025
  }

  },[isSuccess,navigate])

  // 7 - Form field updates / change handling
  const handleChange = (e) => {
  const { name, value, files } = e.target
  if (name === "pic") {
    setFormData(prevState => ({ ...prevState, pic: files[0] }))
  } else {
    setFormData(prevState => ({ ...prevState, [name]: value }))
  }
  }


// 7a - New function to handle adding selected section
const handleAddSection = () => {
  //console.log("Attempting to add section with ID:", selectedSection);
  const sectionToAdd = sections.find(section => section._id.toString() === selectedSection);
  //console.log("Found section to add:", sectionToAdd);

  if (sectionToAdd && !formData.sections.some(sec => sec._id.toString() === selectedSection)) {
    setFormData(prevState => ({
      ...prevState,
      sections: [...prevState.sections, sectionToAdd]
    }));
    setSelectedSection('');
  }
}



// 7b - New function to handle removing a section
const handleRemoveSection = (sectionId) => {
  setFormData(prevState => ({
    ...prevState,
    sections: prevState.sections.filter(sec => sec._id !== sectionId)
  }))
  
}

useEffect(() => {
  if (formData.sections.length === 0) {
    setSelectedSection(''); // Reset select box whenever sections are empty
  }
}, [formData.sections]);

// 7c - handle select change for section
const handleSelectChange = (e) => {
  setSelectedSection(e.target.value);
}

useEffect(() => {
  // Check if all sections from the set have been added
  setAllSectionsAdded(sections?.length === formData.sections?.length);
}, [formData.sections, sections?.length]);


// 8 - Form Submit Logic
const handleSubmit = async (e) => {
  e.preventDefault()
  
  // Initialize an object to collect the form data
  const assessmentData = {
    assessmentName: formData.assessmentName,
    assessmentIntro: formData.assessmentIntro,
    assessmentOutro: formData.assessmentOutro,
    aboutMe: formData.aboutMe,
    sections: formData.sections,
  }
  
  //console.log('assessment Name:',assessmentData.assessmentName)
 // console.log('sections:', formData.sections)
  
  if (formData.pic) {
    // Convert the image to a base64 string
    const reader = new FileReader()
    reader.onloadend = async () => {
      // Once conversion is done, add the base64 string to your data object
      assessmentData.pic = reader.result
  
      // Now, send the data object to the server
      try {
        assessmentData.id = id
        console.log("Form submitted.")
        await updateAssessment(assessmentData).unwrap() // Dispatch the RTK Query mutation
      } catch (error) {
        console.error(error)
      }
    }
    reader.readAsDataURL(formData.pic)
  } else {
    // No image, send other data as is
    try {
      assessmentData.id = id
      console.log("Form submitted.")
      await updateAssessment(assessmentData).unwrap() // Dispatch the RTK Query mutation
    } catch (error) {
      console.error(error)
    }
  }
  }
  
  // 9 - Handle Cancel
  
  const handleCancel = () => {
    // navigate("/assessments")
    setActiveAdminView('assessments'); // 19 Aug 2025
  }
  
  // 10 - Handle Clear
  const handleClear = () => {
    setFormData({
      assessmentName: "",
      assessmentIntro: "",
      assessmentOutro: "",
      aboutMe: "",
      pic: "",
      sections: [],
    })
  }

  return (
    <div className="bg-gray-200 p-1 mx-auto mt-5 rounded-lg shadow-md shadow-black">
      {isLoading && <PulseLoader color={"#ea580c"} />}
      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}
      <div className="bg-white p-1 m-1 rounded">
        <p className="p-1 m-1 font-bold font-poppins text-xl">Add an Assessment</p>
  
        <div className="bg-gray-200 rounded m-1 p-1">
          <form
            onSubmit={handleSubmit}
            className="m-1 p-1 text-xs font-poppins"
          >
            
            <div id="assessmentName">
              <input
                type="text"
                name="assessmentName"
                placeholder="Enter the assessment name here"
                value={formData.assessmentName}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>
  
            <div className="bg-white w-full rounded p-2 m-1">
              <EtContentEditorLightWithPlace
                value={formData.assessmentIntro}
                handleChange={(content) =>
                  setFormData({ ...formData, assessmentIntro: content })
                }
                placeholderText={
                  "Write what people would see when this assessment starts!"
                }
                // toolbarId={'sectionIntro'}
              />
            </div>
  
            <div className="bg-white w-full rounded p-2 m-1">
              <EtContentEditorLight3
                value={formData.assessmentOutro}
                handleChange={(content) =>
                  setFormData({ ...formData, assessmentOutro: content })
                }
                placeholderText={
                  "Write what people would see when this assessment ends!"
                }
                toolbarId={"assessmentOurtro"}
              />
            </div>
  
            <div id="aboutMe">
              <input
                type="text"
                name="aboutMe"
                placeholder="Add something about the personality about this assessment"
                value={formData.aboutMe}
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>
  
            <div id="pic">
              <input
                type="file"
                name="pic"
                onChange={handleChange}
                className="w-full rounded p-2 m-1"
              />
            </div>
  
            {/* Section Selector and Add Button */}
            <div>
              <div className="flex justify-between items-center my-2">
                <select
                  onChange={handleSelectChange}
                  value={selectedSection}
                  className="w-full rounded p-2 mr-2"
                >
                  {sectionOptions}
                </select>
                {!allSectionsAdded && (
                <button
                  type="button"
                  onClick={handleAddSection}
                  className="py-2 px-4 rounded bg-orange-600 text-white"
                >
                  +
                </button>)}
              </div>
  
              {/* Display Selected Sections with Remove Option */}
              {formData.sections?.map((section, index) => (
                <div
                  key={section._id+index}
                  className="flex justify-between items-center bg-gray-100 p-2 rounded my-1"
                >
                  <span className="text-black">
                    {stripHtml(section.sectionName)}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveSection(section._id)}
                    className="py-1 px-3 rounded bg-red-500 text-white"
                  >
                    -
                  </button>
                </div>
              ))}
            </div>
  
            {/* 15 Apr 2024 - Assessment Options */}
  
            <div id="actions" className="bg-white m-1 px-1 py-3 rounded">
              <EtBtnFormBasic
                name={"Update"}
                type={"create"}
                handleClick={handleSubmit}
              />
              <EtBtnFormBasic
                name={"Clear"}
                type={"clear"}
                handleClick={handleClear}
              />
              <EtBtnFormBasic
                name={"Cancel"}
                type={"cancel"}
                handleClick={handleCancel}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  )

}

export default EtUpdateAssessment